import { useState, useEffect, useCallback } from 'react';
import { useVersionedApi } from '../utils/versionedApi.js';

import { safeStorage } from '../utils/safeStorage';
import { useAuth } from '../AuthContext';
/**
 * Modern alerts hook with proper error handling and state management
 * Skips alerts for clinical-only roles (MHP, RN, LPN) who don't manage visits
 */
export const useAlerts = () => {
  const { user, authChecked } = useAuth();
  // Platform admins hold no organizationId, so every tenant-scoped call 401s.
  const isPlatformAdmin = !!user && user.organizationId === null;
  const [visitAlertsEnabled, setVisitAlertsEnabled] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const apiClient = useVersionedApi('v1');

  // Resolve bundle access before the global alert provider calls any visit
  // endpoint. AlertsProvider mounts above AdminShell, so reading the bundle
  // cache here races AdminShell's /bundles/my-bundles request on a fresh login.
  useEffect(() => {
    let active = true;

    setVisitAlertsEnabled(false);
    setAlerts([]);
    setAlertCount(0);
    setError(null);
    setLastFetched(null);
    // This effect runs before AuthContext's session check (child effects run
    // first), so wait for it, and never send a token-less request for a cached
    // user: it can only 400 NO_ORGANIZATION_CONTEXT.
    if (authChecked === false || !user?.organizationId || !safeStorage.getItem('evv_token')) {
      return () => { active = false; };
    }

    const resolveVisitAlertAccess = async () => {
      try {
        const response = await apiClient.get('/bundles/my-bundles');
        if (!active) return;

        const slugs = (response.data?.bundles || [])
          .map((assignment) => assignment.bundle?.slug)
          .filter(Boolean);
        setVisitAlertsEnabled(
          slugs.includes('home-health') || slugs.includes('complete-care')
        );
      } catch {
        // Visit alerts are optional. If bundle resolution is unavailable, do
        // not probe a Home Health-only endpoint for an unrelated tenant.
        if (active) setVisitAlertsEnabled(false);
      }
    };

    resolveVisitAlertAccess();
    return () => { active = false; };
  }, [apiClient, user?.organizationId, authChecked]);

  // Check if the user's role and resolved bundle support visit alerts.
  const shouldFetchAlerts = useCallback(() => {
    if (!user || !visitAlertsEnabled) return false;

    const clinicalOnlyTypes = ['MHP', 'RN', 'LPN'];
    if (user.role === 'provider' && clinicalOnlyTypes.includes(user.providerType)) {
      return false;
    }

    return true;
  }, [user, visitAlertsEnabled]);

  // Fetch alert count (for the bell icon badge) - only used as fallback
  const fetchAlertCount = useCallback(async () => {
    // Skip alerts for clinical-only roles
    if (!shouldFetchAlerts()) {
      return;
    }
    
    try {
      setError(null);
      const token = safeStorage.getItem("evv_token");
      if (!token) return;
      // Visit alerts are tenant data; a platform admin (organizationId null)
      // cannot pass tenantMiddleware, so this only ever 401s for them.
      if (isPlatformAdmin) return;

      const response = await apiClient.get('/visits/alerts/count');
      
      // Only update count if we don't have alerts data loaded
      // This prevents overriding the more accurate count from fetchAlerts
      setAlertCount(prev => {
        const newCount = response.data.unreviewedFlaggedVisits || 0;
        return alerts.length === 0 ? newCount : prev;
      });
      setLastFetched(Date.now());
    } catch (err) {
      // 403 means org doesn't have the required bundle — silently skip
      if (err.response?.status === 403) {
        return;
      }
      console.error("Failed to fetch alert count:", err);
      if (err.response?.status === 401) {
        setError('Unauthorized');
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout');
      } else {
        setError(err.message || 'Failed to fetch alerts');
      }
      // Don't reset count on error, keep last known value
    }
  }, [apiClient, alerts.length, shouldFetchAlerts, isPlatformAdmin]);

  // Fetch detailed alerts (for the dropdown)
  const fetchAlerts = useCallback(async () => {
    // Skip alerts for clinical-only roles
    if (!shouldFetchAlerts()) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const token = safeStorage.getItem("evv_token");
      if (!token) return;

      // Use the alerts route instead of the non-existent preview route
      const response = await apiClient.get('/alerts');
      
      setAlerts(response.data || []);
      // Count only unread alerts for the badge - this should be the source of truth
      const unreadCount = (response.data || []).filter(alert => !alert.read).length;
      setAlertCount(unreadCount);
      setLastFetched(Date.now());
    } catch (err) {
      // 403 means org doesn't have the required bundle — silently skip
      if (err.response?.status === 403) {
        setAlerts([]);
        return;
      }
      console.error("Failed to fetch alerts:", err);
      if (err.response?.status === 401) {
        setError('Unauthorized');
      } else if (err.response?.status === 404) {
        setError('Alerts endpoint not found');
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timeout');
      } else {
        setError(err.message || 'Failed to fetch alerts');
      }
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, shouldFetchAlerts]);

  // Mark alert as read
  const markAsRead = useCallback(async (alertId) => {
    try {
      const token = safeStorage.getItem("evv_token");
      if (!token) return;

      await apiClient.patch(`/alerts/${alertId}/read`, {});
      
      // Update local state - mark alert as read, don't remove it
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, read: true }
          : alert
      ));
      
      // Decrease unread count by 1
      setAlertCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark alert as read:", err);
      throw err;
    }
  }, [apiClient]);

  // Bulk mark alerts as read
  const bulkMarkAsRead = useCallback(async (alertIds = null, alertType = null) => {
    try {
      const token = safeStorage.getItem("evv_token");
      if (!token) return;

      const payload = {};
      if (alertIds) payload.alertIds = alertIds;
      if (alertType) payload.alertType = alertType;

      const response = await apiClient.patch('/alerts/bulk-mark-read', payload);
      
      // Update local state - mark matching alerts as read
      setAlerts(prev => prev.map(alert => {
        const shouldMarkAsRead = alertIds 
          ? alertIds.includes(alert.id)
          : alertType 
            ? alert.type === alertType
            : !alert.read; // If no filter, mark all unread as read
        
        return shouldMarkAsRead 
          ? { ...alert, read: true }
          : alert;
      }));
      
      // Update alert count based on what was marked as read
      const updatedCount = response.data.updatedCount || 0;
      setAlertCount(prev => Math.max(0, prev - updatedCount));
      
      return response.data;
    } catch (err) {
      console.error("Failed to bulk mark alerts as read:", err);
      throw err;
    }
  }, [apiClient]);

  // Refresh alerts (force refresh)
  const refreshAlerts = useCallback(async () => {
    setLastFetched(null); // Clear cache
    await fetchAlertCount();
    await fetchAlerts();
  }, [fetchAlertCount, fetchAlerts]);

  // Auto-fetch count on mount and periodically
  useEffect(() => {
    const token = safeStorage.getItem("evv_token");
    
    // Don't fetch if no token (user not logged in)
    if (!token) {
      return;
    }
    
    const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
    const shouldFetch = !lastFetched || (Date.now() - lastFetched) > CACHE_DURATION;
    
    if (shouldFetch) {
      fetchAlertCount();
    }

    // Set up periodic refresh (every 5 minutes) - only if user is logged in
    const interval = setInterval(() => {
      const currentToken = safeStorage.getItem("evv_token");
      if (currentToken) {
        fetchAlertCount();
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchAlertCount, lastFetched]);

  return {
    alerts,
    alertCount,
    loading,
    error,
    fetchAlerts,
    fetchAlertCount,
    markAsRead,
    bulkMarkAsRead,
    refreshAlerts,
    isStale: lastFetched && (Date.now() - lastFetched) > (2 * 60 * 1000) // 2 minutes
  };
};
